import type { JSX } from "react";

import type { LegendType } from "recharts";
import { Surface, Symbols } from "recharts";
import type { SymbolType } from "recharts/types/util/types";
import { twMerge } from "tailwind-merge";

import type { ChartSeries } from "./utils";

// Matches recharts' DefaultLegendContent icon box so the icons render identically.
const ICON_SIZE = 14;

/**
 * Renders a single legend icon using the same geometry recharts uses internally
 * (see recharts DefaultLegendContent#renderIcon), so `iconType="line"` looks
 * exactly like the line-chart legend icon.
 */
const LegendIcon = ({
  type,
  color
}: {
  type: LegendType;
  color: string;
}): JSX.Element | null => {
  if (type === "none") return null;

  const halfSize = ICON_SIZE / 2;
  const sixthSize = ICON_SIZE / 6;
  const thirdSize = ICON_SIZE / 3;

  let icon: JSX.Element;
  if (type === "plainline") {
    icon = (
      <line
        strokeWidth={4}
        fill="none"
        stroke={color}
        x1={0}
        y1={halfSize}
        x2={ICON_SIZE}
        y2={halfSize}
        className="recharts-legend-icon"
      />
    );
  } else if (type === "line") {
    icon = (
      <path
        strokeWidth={2}
        fill="none"
        stroke={color}
        d={`M0,${halfSize}h${thirdSize}A${sixthSize},${sixthSize},0,1,1,${
          2 * thirdSize
        },${halfSize}H${ICON_SIZE}M${
          2 * thirdSize
        },${halfSize}A${sixthSize},${sixthSize},0,1,1,${thirdSize},${halfSize}`}
        className="recharts-legend-icon"
      />
    );
  } else if (type === "rect") {
    // Centered square (not a full-width rectangle) so the swatch reads as a box.
    const side = (ICON_SIZE * 3) / 4;
    const offset = (ICON_SIZE - side) / 2;
    icon = (
      <path
        stroke="none"
        fill={color}
        d={`M${offset},${offset}h${side}v${side}h${-side}z`}
        className="recharts-legend-icon"
      />
    );
  } else {
    icon = (
      <Symbols
        fill={color}
        cx={halfSize}
        cy={halfSize}
        size={halfSize}
        sizeType="diameter"
        type={type as SymbolType}
      />
    );
  }

  return (
    <Surface
      width={ICON_SIZE}
      height={ICON_SIZE}
      viewBox={{ x: 0, y: 0, width: ICON_SIZE, height: ICON_SIZE }}
      className="flex-shrink-0"
    >
      {icon}
    </Surface>
  );
};

interface ChartLegendProps {
  /** One entry per series (bar group / line). */
  series: ChartSeries[];
  /** Extra classes for the legend container. */
  className?: string;
  /** recharts legend icon shape (e.g. "rect", "line", "circle"). Defaults to "rect". */
  iconType?: LegendType;
  /** Tint the label text with the series color. */
  colorLabel?: boolean;
  /**
   * Aggregation applied to the measure (e.g. "SUM"). When the chart has a single
   * series (no grouping), it is appended to the label as `Measure (SUM)`.
   */
  aggregation?: string;
}

/**
 * Shared chart legend for vertical / horizontal / line charts.
 *
 * Scrolls vertically once the entries overflow (`max-h-32` + `overflow-y-auto`),
 * mirroring the DonutChart legend so no chart's legend grows unbounded. Icons are
 * rendered via recharts so any `LegendType` (including "line") is supported.
 */
const ChartLegend = ({
  series,
  className,
  iconType = "rect",
  colorLabel = false,
  aggregation
}: ChartLegendProps) => {
  if (!series.length) return null;

  // Only a single-series (ungrouped) chart appends the aggregation to its label.
  const isSingleSeries = series.length === 1;

  return (
    <section
      aria-label="Chart legend"
      className={twMerge(
        "legends flex flex-wrap justify-center gap-x-2 gap-y-1 px-2",
        "max-h-32 overflow-y-auto custom-scrollbar w-full shrink-0",
        className
      )}
    >
      {series.map((s) => {
        const baseLabel = s.label ?? s.dataKey;
        const label =
          isSingleSeries && aggregation
            ? `${baseLabel} (${aggregation})`
            : baseLabel;
        return (
          <div key={s.dataKey} className="flex items-center gap-1.5">
            <LegendIcon type={iconType} color={s.color} />
            <span
              className="text-xs"
              style={colorLabel ? { color: s.color } : undefined}
              title={label}
            >
              {label}
            </span>
          </div>
        );
      })}
    </section>
  );
};

export default ChartLegend;
